import { Formik } from "formik";
import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import * as yup from "yup";
import styled from "styled-components";

import { Button } from "src/library/components/Button";
import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { ButtonLink } from "src/library/components/Link";
import Grid from "src/library/components/Hedron";
import CustomerInfoSidebar from "src/core/customers/components/CustomerInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  ERPSeedless,
  ERPRJL
} from "src/utils/constants";
import {
  ICustomer,
  useCustomerApi
} from "src/core/customers/api/customers.api";
import { ERPType } from "src/core/shared/api/globalSettings.api";
import { setCustomer } from "src/core/customers/store/customers.actions";
import type { IStore } from "src/core/shared/store";
import { useCustomer } from "src/core/customers/hooks";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

type UrlParams = {
  customerId: string;
};

const updateCustomerForm: (customer: ICustomer, erp: ERPType | null, canAmend: boolean) => IFormField[] = (customer, erp, canAmend) => {
  // For disabling form fields
  const disabled = !canAmend || (erp !== ERPSeedless && erp !== ERPRJL);
  let fields: IFormField[] = [
    {
      type: "input",
      name: "accountNumber",
      label: "Account Number",
      placeholder: "Account Number",
      value: customer.accountNumber,
      disabled,
      validation: yup
        .string()
        .min(1)
        .required("Account number is required")
    },
    {
      type: "input",
      name: "name",
      label: "Name",
      placeholder: "Name",
      value: customer.name,
      disabled,
      validation: yup
        .string()
        .min(1)
        .required("Name is required")
    },
    {
      type: "fieldset",
      name: "address",
      label: "Address",
      fields: [
        {
          type: "input",
          name: "addressLine1",
          label: "Address line 1",
          placeholder: "Address line 1",
          value: customer.address.addressLine1,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "addressLine2",
          label: "Address line 2",
          placeholder: "Address line 2",
          value: customer.address.addressLine2,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "addressLine3",
          label: "Address line 3",
          placeholder: "Address line 3",
          value: customer.address.addressLine3,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "city",
          label: "City",
          placeholder: "City",
          value: customer.address.city,
          disabled,
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "postcode",
          label: "Postcode",
          placeholder: "Postcode",
          value: customer.address.postcode,
          disabled,
          validation: yup.string().notRequired()
        }
      ]
    }
  ];

  if (erp === ERPSeedless || erp === ERPRJL) {
    fields = _.concat(fields, [
      {
        type: "input",
        name: "contactName",
        label: "Contact Name",
        placeholder: "Contact Name",
        value: customer.contactName,
        disabled,
        validation: yup.string().notRequired()
      },
      {
        type: "input",
        name: "contactNumber",
        label: "Contact Number",
        placeholder: "Contact Number",
        value: customer.contactNumber,
        disabled,
        validation: yup.string().notRequired()
      },
      {
        type: "input",
        name: "contactEmail",
        label: "Contact Email",
        placeholder: "Contact Email",
        value: customer.contactEmail,
        disabled,
        validation: yup.string().notRequired()
      }
    ]);
  }

  return fields;
};

const EditCustomerPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { match, terminology } = props;
  const dispatch = useDispatch();
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const [ updateCustomerResponse, updateCustomer ] = useCustomerApi("UPDATE_CUSTOMER");
  const formRef = React.useRef<Formik>(null);
  const { isLoading: isLoadingCustomer, customer } = useCustomer(customerId);
  const checkPermission = useCheckUserPermission();

  const canAmend = React.useMemo(() => {
    return checkPermission("amend-customer");
  }, [ checkPermission ]);

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));

  React.useEffect(() => {
    if (updateCustomerResponse.data) {
      dispatch(setCustomer(updateCustomerResponse.data));
      toast.success("Saved!");
    }
  }, [ updateCustomerResponse.data ]);

  return (
    <Layout
      {...props}
      title={customer ? customer.name : ""}
    >
      <MainContentWrapper fill>
        <FormWrapper>
          {!isLoadingCustomer && customer && (
            <Form
              ref={formRef}
              error={updateCustomerResponse.error ? updateCustomerResponse.error.message : null}
              submitting={updateCustomerResponse.pending}
              fields={updateCustomerForm(customer, erp as ERPType, canAmend)}
              onSubmit={data => {
                const formData = Object.assign({}, data);

                // Structure the data to how we need to send it
                formData.address = {
                  addressLine1: formData.addressLine1,
                  addressLine2: formData.addressLine2,
                  addressLine3: formData.addressLine3,
                  city: formData.city,
                  postcode: formData.postcode
                };
                // Remove incorrectly structured data
                delete formData.dropName;
                delete formData.addressLine1;
                delete formData.addressLine2;
                delete formData.addressLine3;
                delete formData.city;
                delete formData.postcode;

                updateCustomer({
                  ...customer,
                  ...formData
                });
              }}
            />
          )}
        </FormWrapper>
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerInfoSidebar
          terminology={terminology}
          isLoading={isLoadingCustomer}
          data={customer}
          primaryAction={(erp === ERPSeedless || erp === ERPRJL) ? (
            <ButtonLink
              $buttonType="secondary"
              to="/admin/customers"
            >
              Cancel
            </ButtonLink>
          ) : (
            undefined
          )}
          secondaryAction={(erp === ERPSeedless || erp === ERPRJL) ? (
            <Button
              loading={updateCustomerResponse.pending}
              onClick={submitForm}
              disabled={!canAmend}
            >
              Save
            </Button>
          ) : (
            undefined
          )}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  form {
    width: 400px;
  }
`;

export default EditCustomerPage;
